import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Box,
  IconButton,
  Typography,
  Stack,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion, AnimatePresence } from "framer-motion";
import { NoticeboardButton } from "../shared";
import FullScreenConfetti from "../shared/FullScreenConfetti";
import { lightTheme } from "../../styles/theme";

interface NoticeFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  message: string;
  isSuccess: boolean;
  isSubmitting?: boolean;
  isConfirmation?: boolean;
  recipientCount?: number;
  audienceName?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  onSaveDraft?: () => void;
  /** Block publish while attachments are still uploading. */
  confirmDisabled?: boolean;
  scheduleMode?: boolean;
  scheduledAtLabel?: string;
  staffOnly?: boolean;
  preview?: React.ReactNode;
}

const NoticeFeedbackModal: React.FC<NoticeFeedbackModalProps> = ({
  open,
  onClose,
  message,
  isSuccess,
  isSubmitting = false,
  isConfirmation = false,
  recipientCount,
  audienceName,
  onConfirm,
  onCancel,
  onSaveDraft,
  confirmDisabled = false,
  scheduleMode = false,
  scheduledAtLabel,
  staffOnly = false,
  preview,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const showMobileReview = isMobile && isConfirmation && !!preview;
  const [showCheckmark, setShowCheckmark] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (open && isSuccess && !isSubmitting) {
      const timer = setTimeout(() => setShowCheckmark(true), 300);
      setShowConfetti(true);
      const confettiTimer = setTimeout(() => setShowConfetti(false), 5000);
      return () => {
        clearTimeout(timer);
        clearTimeout(confettiTimer);
      };
    } else {
      setShowCheckmark(false);
      setShowConfetti(false);
    }
  }, [open, isSuccess, isSubmitting]);

  return (
    <>
      {showConfetti && <FullScreenConfetti duration={5000} />}

      <AnimatePresence>
        {open && (
          <Dialog
            open={open}
            onClose={isSubmitting ? undefined : (isConfirmation ? (onCancel || onClose) : onClose)}
            maxWidth="xs"
            fullWidth
            disableEscapeKeyDown={isSubmitting}
            disableScrollLock
            sx={{
              "& .MuiDialog-paper": {
                animation: "slideUp 0.3s ease-out",
                minHeight: 340,
              },
              "@keyframes slideUp": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(20px) scale(0.95)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0) scale(1)",
                },
              },
            }}
          >
            {showMobileReview && (
              <IconButton aria-label="Close preview and return to editing" onClick={onCancel || onClose} sx={{ position: "absolute", top: 8, right: 8, color: "text.secondary", zIndex: 1 }}>
                <CloseIcon />
              </IconButton>
            )}
            <DialogTitle
              sx={{
                color: isConfirmation
                  ? theme.palette.primary.main
                  : isSubmitting
                  ? theme.palette.primary.main
                  : isSuccess
                  ? theme.palette.success.main
                  : theme.palette.error.main,
                textAlign: "center",
                pt: 3,
                ...(showMobileReview && { px: 6, fontWeight: 400 }),
                ...(isConfirmation && { fontSize: "1.5rem" }),
              }}
            >
              {isConfirmation
                ? showMobileReview
                  ? "Review your notice"
                  : scheduleMode
                  ? "Ready to schedule?"
                  : "We're all set to go."
                : isSubmitting
                ? scheduleMode
                  ? "Scheduling Notice..."
                  : "Publishing Notice..."
                : isSuccess
                ? "Success!"
                : "Couldn't publish notice"}
            </DialogTitle>
            <DialogContent sx={{ textAlign: "center", py: 3 }}>
              {showMobileReview ? (
                <Box sx={{ mb: 3, textAlign: "left" }}>{preview}</Box>
              ) : <Box
                sx={{
                  marginBottom: 3,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 120,
                }}
              >
                <AnimatePresence mode="wait">
                  {isConfirmation ? (
                    <motion.div
                      key="confirmation"
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.9, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src="/icons/lost.webp"
                        alt="Confirm publish"
                        loading="lazy"
                        decoding="async"
                        sx={{
                          width: 140,
                          height: 140,
                          objectFit: "contain",
                        }}
                      />
                    </motion.div>
                  ) : isSubmitting ? (
                    <motion.div
                      key="sending"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <motion.div
                        animate={{
                          rotate: 360,
                          scale: [1, 1.1, 1],
                        }}
                        transition={{
                          rotate: {
                            duration: 2,
                            repeat: Infinity,
                            ease: "linear",
                          },
                          scale: {
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src="/icons/fast.webp"
                          alt="Publishing"
                          loading="lazy"
                          decoding="async"
                          sx={{
                            width: 140,
                            height: 140,
                            objectFit: "contain",
                          }}
                        />
                      </motion.div>
                    </motion.div>
                  ) : isSuccess && showCheckmark ? (
                    <motion.div
                      key="success"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 200,
                        damping: 15,
                        duration: 0.6,
                      }}
                    >
                      <CheckCircleIcon
                        sx={{
                          fontSize: 80,
                          color: theme.palette.success.main,
                          filter: `drop-shadow(0 4px 8px ${theme.palette.textShadows.medium})`,
                        }}
                      />
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </Box>}
              {showMobileReview ? (
                <Stack spacing={1.5} sx={{ textAlign: "left" }}>
                  <Box><Typography variant="subtitle2">Who receives it</Typography><Typography variant="body2">{staffOnly ? "Internal team only · Members will not be notified" : `${audienceName || "Selected audience"} · ${recipientCount ?? 0} ${(recipientCount ?? 0) === 1 ? "user" : "users"}`}</Typography></Box>
                  <Box><Typography variant="subtitle2">When</Typography><Typography variant="body2">{scheduleMode ? scheduledAtLabel || "The chosen date" : "Immediately"}</Typography></Box>
                </Stack>
              ) : <motion.p
                key={isConfirmation ? "confirmation" : isSubmitting ? "sending" : isSuccess ? "success" : "error"}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                style={{
                  margin: 0,
                  fontSize: "1rem",
                  color: theme.palette.text.primary,
                }}
              >
                {isConfirmation && scheduleMode ? (
                  staffOnly ? (
                    <>
                      This event will be added to your internal team calendar on{" "}
                      <Box
                        component="span"
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                        }}
                      >
                        {scheduledAtLabel || "the chosen date"}
                      </Box>
                      . Members will not be notified.
                    </>
                  ) : (
                    <>
                      This notice will go live on{" "}
                      <Box
                        component="span"
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                        }}
                      >
                        {scheduledAtLabel || "the chosen date"}
                      </Box>{" "}
                      and notify{" "}
                      <Box
                        component="span"
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          color: theme.palette.error.main,
                        }}
                      >
                        {recipientCount ?? 0}{" "}
                        {(recipientCount ?? 0) === 1 ? "user" : "users"}
                      </Box>
                      {audienceName ? (
                        <>
                          {" "}
                          in{" "}
                          <Box
                            component="span"
                            sx={{
                              fontSize: "1.1rem",
                              fontWeight: 600,
                              color: theme.palette.primary.main,
                            }}
                          >
                            {audienceName}
                          </Box>
                        </>
                      ) : null}
                      .
                    </>
                  )
                ) : isConfirmation && recipientCount !== undefined ? (
                  <>
                    By clicking publish, please take note that this notice will be published live to{" "}
                    <Box
                      component="span"
                      sx={{ fontSize: "1.25rem", fontWeight: 600, color: theme.palette.error.main }}
                    >
                      {recipientCount} {recipientCount === 1 ? "user" : "users"}
                    </Box>
                    {audienceName ? (
                      <>
                        {" "}
                        in{" "}
                        <Box
                          component="span"
                          sx={{
                            fontSize: "1.25rem",
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                          }}
                        >
                          {audienceName}
                        </Box>
                      </>
                    ) : null}
                    .
                  </>
                ) : (
                  message
                )}
              </motion.p>}
            </DialogContent>
            {isConfirmation ? (
              <DialogActions sx={{ justifyContent: "center", flexWrap: "wrap", pb: 2, px: 3, gap: 2 }}>
                {onSaveDraft && (
                  <NoticeboardButton
                    variant="secondary"
                    size="medium"
                    onClick={onSaveDraft}
                    sx={{ flex: 1, minWidth: 100, transition: "all 0.3s ease" }}
                  >
                    Save draft
                  </NoticeboardButton>
                )}
                <NoticeboardButton
                  variant="primary"
                  size="medium"
                  onClick={onConfirm}
                  disabled={confirmDisabled}
                  sx={{
                    flex: 1,
                    minWidth: 100,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 4px 12px ${lightTheme.colors.primaryTint40}`,
                    },
                  }}
                >
                  {confirmDisabled
                    ? "Uploading…"
                    : scheduleMode
                      ? "Schedule"
                      : "Publish"}
                </NoticeboardButton>
              </DialogActions>
            ) : !isSubmitting && (
              <DialogActions sx={{ justifyContent: "center", pb: 2, px: 3 }}>
                <NoticeboardButton
                  variant="primary"
                  size="medium"
                  fullWidth
                  onClick={onClose}
                  sx={{
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 4px 12px ${lightTheme.colors.primaryTint40}`,
                    },
                  }}
                >
                  {isSuccess ? "Continue" : "Close"}
                </NoticeboardButton>
              </DialogActions>
            )}
          </Dialog>
        )}
      </AnimatePresence>
    </>
  );
};

export default NoticeFeedbackModal;
